import React from 'react';

import { Money, formatMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { track } from '@betterment/js-runtime';
import { Box, Card, H2, Separator, Text, ToggleTip } from '@betterment/samba-react';
import { CardLoader, ErrorBoundary, SambaAutoLink } from '@betterment/shared-components';

import { DividendsAndInterestDocument } from './graphql/dividendsAndInterest.generated';

import { DateRangeOption } from '../../../../graphql/schema';
import { urlBuilder } from '../../../../Shared/urlBuilder';
import { dateRangeFilterByValue } from '../../../shared/date-range-filter/DateRangeFilter';

const IncomeToggletip = () => {
  const handleOpenChange = (open: boolean) => {
    if (open) {
      track('ElementClicked', {
        name: 'incomeTooltip',
        module: 'DividendsAndInterest',
        location: 'AccountPerformance',
      });
    }
  };

  return (
    <ToggleTip onOpenChange={handleOpenChange}>
      <ToggleTip.Trigger label="Income information" css={{ ml: 'space.50', verticalAlign: 'text-top' }} />
      <ToggleTip.Content align="start" side="left" css={{ maxWidth: '300px', width: 'max-content' }}>
        The total interest and dividends you earned over a specified time period.
      </ToggleTip.Content>
    </ToggleTip>
  );
};

interface DividendsAndInterestCardViewProps {
  dateRangeOption?: DateRangeOption;
}

const DividendsAndInterestCardView: React.FC<DividendsAndInterestCardViewProps> = ({
  dateRangeOption = DateRangeOption.AllTime,
}) => {
  const { data } = useGraphQLQuery(DividendsAndInterestDocument, {
    variables: { dateRangeOption },
    throwOnError: true,
  });

  if (!data) return <CardLoader />;

  const { investing, interest } = data.performance.totals;

  if (!investing?.dividends && !interest)
    return (
      <Card>
        <Card.Header css={{ padding: 'space.300' }}>
          <H2 variant="title200">Income earned</H2>
        </Card.Header>
        <Separator css={{ fullWidthWithInset: 'space.300' }} />
        <Card.Content css={{ padding: 'space.300' }}>
          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.50' }}>
            <Text>You don't have any accounts earning dividends or interest.</Text>
            <SambaAutoLink to={urlBuilder.addGoal.absolute()} variant="textPrimary">
              Open an account
            </SambaAutoLink>
          </Box>
        </Card.Content>
      </Card>
    );

  const cashInterest = interest ? interest.paid : Money(0);
  const dividends = investing?.dividends ? investing.dividends.total : Money(0);
  const totalIncome = Money(dividends.cents + cashInterest.cents);
  const dateRangeLabel = dateRangeFilterByValue(dateRangeOption)?.prefixedDescription('Income') || '';

  return (
    <Card>
      <Card.Header css={{ padding: 'space.300' }}>
        <H2 variant="title200">Income earned</H2>
      </Card.Header>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
      <Card.Content css={{ padding: 'space.300', display: 'flex', flexDirection: 'column', gap: 'space.50' }}>
        <Box css={{ display: 'flex', flexDirection: 'column', marginBottom: 'space.50' }}>
          <Text>
            {dateRangeLabel}
            <IncomeToggletip />
          </Text>
          <Text variant="text300" css={{ color: totalIncome.cents > 0 ? 'text.success' : 'text.default' }}>
            {formatMoney(totalIncome)}
          </Text>
        </Box>
        <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text>Cash interest</Text>
          <Text>{formatMoney(cashInterest)}</Text>
        </Box>
        <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text>Dividends</Text>
          <Text>{formatMoney(dividends)}</Text>
        </Box>
      </Card.Content>
    </Card>
  );
};

export const DividendsAndInterestCard: React.FC<DividendsAndInterestCardViewProps> = props => (
  <ErrorBoundary fallbackType="none">
    <DividendsAndInterestCardView {...props} />
  </ErrorBoundary>
);
